import { motion, useSpring, useTransform, useMotionValue, MotionValue } from "motion/react";
import { ArrowDownRight } from "lucide-react";
import { useEffect } from "react";

// Deterministic PRNG (mulberry32, fixed seed) in place of Math.random(). This
// module is evaluated twice for a pre-rendered page — once server-side during
// the SSG build, once client-side when the browser loads the bundle — and
// Math.random() would produce a different particle field each time, causing
// a hydration mismatch on first paint (particles visibly "jumping" as React
// discards the server-rendered values). Same seed both times means the same
// array both times, with no change to the visual distribution.
function mulberry32(seed: number) {
  return function random() {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const random = mulberry32(42);

const particles = Array.from({ length: 40 }).map((_, i) => ({
  id: i,
  top: `${random() * 100}%`,
  left: `${random() * 100}%`,
  size: random() * 4 + 2, // 2px to 6px
  parallaxFactorX: (random() - 0.5) * 150, // -75 to 75
  parallaxFactorY: (random() - 0.5) * 150,
  opacity: random() * 0.4 + 0.1, // 0.1 to 0.5
  isAccent: random() > 0.8, // 20% chance to be accent color
}));

function Particle({ p, smoothX, smoothY }: { p: typeof particles[0], smoothX: MotionValue<number>, smoothY: MotionValue<number> }) {
  const x = useTransform(smoothX, [-1, 1], [-p.parallaxFactorX, p.parallaxFactorX]);
  const y = useTransform(smoothY, [-1, 1], [-p.parallaxFactorY, p.parallaxFactorY]);
  
  return (
    <motion.div
      style={{ 
        x, 
        y, 
        top: p.top, 
        left: p.left, 
        width: p.size, 
        height: p.size, 
        opacity: p.opacity 
      }}
      className={`absolute rounded-full pointer-events-none ${p.isAccent ? 'bg-brand-accent' : 'bg-brand-light'}`}
    />
  );
}

function HeroParticles({ smoothX, smoothY }: { smoothX: MotionValue<number>, smoothY: MotionValue<number> }) {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {particles.map((p) => (
        <Particle key={p.id} p={p} smoothX={smoothX} smoothY={smoothY} />
      ))}
    </div>
  );
}

export function Hero() {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smooth spring animation for the mouse movement
  const springConfig = { damping: 50, stiffness: 400 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Normalize mouse coordinates from -1 to 1
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = (e.clientY / window.innerHeight) * 2 - 1;
      mouseX.set(x);
      mouseY.set(y);
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  // Parallax transforms for floating elements
  const y1 = useTransform(smoothY, [-1, 1], [-60, 60]);
  const x1 = useTransform(smoothX, [-1, 1], [-60, 60]);
  
  const y2 = useTransform(smoothY, [-1, 1], [60, -60]);
  const x2 = useTransform(smoothX, [-1, 1], [60, -60]);

  return (
    <section id="hero" className="relative min-h-screen flex flex-col justify-center pt-24 pb-12 overflow-hidden">
      {/* Interactive Background Glows */}
      <motion.div style={{ x: x1, y: y1 }} className="absolute top-[10%] right-[10%] w-64 h-64 md:w-96 md:h-96 rounded-full bg-brand-accent/20 blur-[120px] mix-blend-screen pointer-events-none" />
      <motion.div style={{ x: x2, y: y2 }} className="absolute bottom-[10%] left-[10%] w-64 h-64 md:w-96 md:h-96 rounded-full bg-brand-light/10 blur-[100px] mix-blend-screen pointer-events-none" />

      {/* Interactive Particles */}
      <HeroParticles smoothX={smoothX} smoothY={smoothY} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col items-center text-center">
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8 md:mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-light/5 border border-brand-light/10 text-brand-light text-xs font-bold uppercase tracking-widest backdrop-blur-sm">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-accent opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-accent"></span>
            </span>
            P2A Marketing & Branding
          </div>
        </motion.div>

        {/* Massive Typography */}
        <h1 className="sr-only">
          Seu negócio está faturando mas não crescendo como deveria — Consultoria de Marketing e Branding P2A
        </h1>
        <div aria-hidden="true" className="flex flex-col items-center justify-center w-full mb-16 md:mb-24 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[8vw] md:text-[5vw] leading-[0.9] font-black uppercase tracking-tighter text-brand-light text-center"
          >
            Seu negócio está faturando
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-[8vw] md:text-[5vw] leading-[0.9] font-black uppercase tracking-tighter text-brand-light flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 text-center mt-2"
          >
            mas não <span className="font-highlight italic text-brand-accent lowercase text-[10vw] md:text-[6vw] font-normal tracking-normal">crescendo</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-[6vw] md:text-[4vw] leading-[0.9] font-bold uppercase tracking-tighter text-brand-light/40 mt-4 md:mt-2 text-center"
          >
            como deveria.
          </motion.div>
        </div>

        {/* Bottom Grid / Info */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-12 w-full max-w-6xl mt-auto text-center md:text-left items-center md:items-end"
        >
          <div className="md:col-span-1 flex flex-col items-center md:items-start">
            <p className="font-highlight text-brand-accent text-7xl md:text-8xl font-bold mb-2 leading-none">+150</p>
            <p className="text-brand-light/60 text-xs md:text-sm font-bold uppercase tracking-widest max-w-[200px]">Clientes satisfeitos em infoprodutos e negócios físicos.</p>
          </div>
          
          <div className="md:col-span-1 flex justify-center relative z-30">
            <a
              href="https://wa.me/5524993144981?text=Vim%20do%20site%20gostaria%20de%20saber%20sobre%20os%20servi%C3%A7os"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex items-center justify-center w-36 h-36 md:w-44 md:h-44 bg-brand-accent text-brand-light font-bold rounded-full hover:scale-105 transition-transform duration-500 shadow-2xl shadow-brand-accent/20"
            >
              <span className="absolute inset-0 rounded-full border border-brand-accent animate-ping opacity-20"></span>
              <div className="flex flex-col items-center gap-1 md:gap-2">
                <span className="text-[10px] md:text-xs uppercase tracking-wider text-center font-display leading-tight px-2">Quero meu<br/>diagnóstico<br/>gratuito</span>
                <ArrowDownRight className="w-5 h-5 md:w-6 md:h-6 group-hover:rotate-[-45deg] transition-transform duration-300" />
              </div>
            </a>
          </div>

          <div className="md:col-span-1 flex flex-col justify-end items-center md:items-end text-center md:text-right">
            <p className="text-sm md:text-base text-brand-light/80 leading-relaxed max-w-[350px] font-medium">
              A P2A é a consultoria criada por empresários que viveram na pele o que é investir em marketing genérico e não ter retorno. Aqui, cada estratégia começa pelo diagnóstico real do seu negócio.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
